import type { Task } from "@app/core";

/** One rendered upcoming day: the agenda day plus, on the day the row budget
 *  runs out, how many of its rows are actually painted. */
export interface UpcomingDay {
  date: Date;
  items: Task[];
  index: number;
  limit?: number | undefined;
}

export interface UpcomingCut {
  upcoming: UpcomingDay[];
  shownRows: number;
  totalRows: number;
}

/** Rows a day actually paints — `DaySection` drops done items when `hideDone`,
 *  so counting raw `items` would spend budget on rows nobody sees. */
export function renderedRowCount(items: Task[], hideDone: boolean): number {
  return hideDone ? items.filter((x) => x.status !== "done").length : items.length;
}

/**
 * Cut the Today agenda's upcoming stream at a cumulative ROW budget.
 *
 * The agenda is un-virtualized like every other list in this app (see
 * `lib/paged-list.tsx`), and it is the app's landing view: on the flat dataset a
 * 7-day horizon is 392 rows at ~32 DOM nodes each, and `/today` mounted **more
 * elements than the dense week calendar** — 14,938 against 9,494.
 *
 * Whole days are kept whole so a section header's open-count and progress bar
 * stay true; only the day the budget runs out on is rendered partially, via
 * `DaySection`'s `limit`. At least one day always renders — an "Upcoming"
 * heading with nothing under it is a worse answer than a long list.
 */
export function cutUpcoming(days: UpcomingDay[], limit: number, hideDone: boolean): UpcomingCut {
  const upcoming: UpcomingDay[] = [];
  let shownRows = 0;
  for (const day of days) {
    const rows = renderedRowCount(day.items, hideDone);
    if (shownRows >= limit && upcoming.length > 0) break;
    const room = limit - shownRows;
    if (rows > room && room > 0) {
      upcoming.push({ ...day, limit: room });
      shownRows += room;
      break;
    }
    upcoming.push(day);
    shownRows += rows;
  }
  const totalRows = days.reduce((n, day) => n + renderedRowCount(day.items, hideDone), 0);
  return { upcoming, shownRows, totalRows };
}

/** Rows needed to render every upcoming day up to and including `index`, so a
 *  week-strip jump can grow the cap to exactly reach its target rather than
 *  scroll to a section that was never rendered. Never shrinks below `floor`. */
export function rowsThroughDay(days: UpcomingDay[], index: number, hideDone: boolean, floor: number): number {
  let n = 0;
  for (const day of days) {
    n += renderedRowCount(day.items, hideDone);
    if (day.index === index) break;
  }
  return Math.max(n, floor);
}
